import React, { useState, useCallback, useMemo, memo } from 'react';
import { Search, Grid as GridIcon, List, MoreVertical, Eye, Clock, Edit, Users, Trash2, TestTube, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import OptimizedCameraThumbnail from './OptimizedCameraThumbnail';

/**
 * Simplified Camera Grid (fallback without react-window)
 * Uses CSS Grid with intersection observer for performance
 */
const SimplifiedCameraGrid = ({ 
  cameras, 
  searchTerm = '', 
  onSearchChange,
  onCameraClick,
  onEditCamera,
  onDeleteCamera,
  onAssignCamera,
  hasPlaybackPermission = false,
  thumbnailKeys = {},
  gridLayout = 'auto'
}) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 20 }); // Show first 20 cameras initially

  // Enhanced search function
  const searchCameras = useCallback((cameras, searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') {
      return cameras;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    const searchWords = searchLower.split(/\s+/).filter(word => word.length > 0);

    return cameras.filter(camera => {
      const searchableText = [
        camera.name || '',
        camera.description || '',
        camera.location?.exactLocation || '',
        camera.location?.city || '',
        camera.location?.state || '',
        camera.location?.country || '',
        camera.ipAddress || '',
        camera.port || '',
        camera.username || '',
        camera.isOnline ? 'online' : 'offline',
        camera.isActive ? 'active' : 'inactive',
        camera.trackNumber ? `track ${camera.trackNumber}` : '',
        camera.assignedUsers?.map(user => user.name || user.username || '').join(' ') || ''
      ].join(' ').toLowerCase();

      return searchWords.every(word => searchableText.includes(word));
    });
  }, []);

  // Apply search
  const filteredCameras = useMemo(() => 
    searchCameras(cameras, searchTerm), 
    [cameras, searchTerm, searchCameras]
  );

  // Get visible cameras based on range
  const visibleCameras = useMemo(() => {
    return filteredCameras.slice(visibleRange.start, visibleRange.end);
  }, [filteredCameras, visibleRange]);

  // Calculate grid classes based on layout
  const getGridClasses = () => {
    if (gridLayout === 'auto') {
      return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';
    }
    
    const [cols] = gridLayout.split('x').map(Number);
    return `grid-cols-${Math.min(cols, 6)}`;
  };

  // Load more cameras when scrolling
  const handleLoadMore = useCallback(() => {
    setVisibleRange(prev => ({
      start: prev.start,
      end: Math.min(prev.end + 20, filteredCameras.length)
    }));
  }, [filteredCameras.length]);

  // Reset visible range when search changes
  useMemo(() => {
    setVisibleRange({ start: 0, end: 20 });
  }, [searchTerm]);

  const toggleDropdown = useCallback((cameraId) => {
    setOpenDropdown(openDropdown === cameraId ? null : cameraId);
  }, [openDropdown]);

  const closeDropdown = useCallback(() => {
    setOpenDropdown(null);
  }, []);

  if (filteredCameras.length === 0) {
    return (
      <div className="space-y-4">
        {/* Search Bar */}
        <div className="flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, location, IP, status, users, track #..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-10 pr-8 py-2 border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 rounded-lg text-sm"
            />
            {searchTerm && (
              <button
                onClick={() => onSearchChange('')}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                title="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Empty State */}
        <div className="text-center py-12 bg-white border border-gray-200 shadow-sm rounded-lg">
          <GridIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {searchTerm ? "No cameras found" : "No cameras available"}
          </h3>
          <p className="text-gray-600">
            {searchTerm
              ? "Try adjusting your search terms or clear the search."
              : "No cameras are available at this time."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="flex-1 max-w-md">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, location, IP, status, users, track #..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-8 py-2 border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 rounded-lg text-sm"
          />
          {searchTerm && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              title="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Search Results Info */}
      {searchTerm && (
        <div className="flex items-center justify-between text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
          <div className="flex items-center space-x-2">
            <Search className="h-4 w-4 text-gray-400" />
            <span>
              Found <span className="font-semibold text-indigo-600">{filteredCameras.length}</span> of <span className="font-semibold">{cameras.length}</span> cameras
            </span>
            {filteredCameras.length > 0 && (
              <span className="text-xs text-gray-500">
                • Search: "{searchTerm}"
              </span>
            )}
          </div>
        </div>
      )}

      {/* CSS Grid (No react-window dependency) */}
      <div className={`grid ${getGridClasses()} gap-4`}>
        {visibleCameras.map((camera, index) => (
          <div
            key={camera._id}
            className="bg-white border border-gray-200 rounded-xl hover:border-indigo-300 hover:shadow-lg transition-all"
          >
            {/* Camera Thumbnail Container */}
            <div className="relative bg-gray-100 rounded-t-xl overflow-hidden h-64">
              <OptimizedCameraThumbnail
                camera={camera}
                refreshKey={thumbnailKeys[camera._id] || 0}
                size="medium"
                priority={index < 10 ? 10 : 0} // Prioritize first 10 cameras
                onError={(cameraId, error) => {
                  console.warn(`Thumbnail error for ${cameraId}:`, error);
                }}
                onClick={() => onCameraClick?.(camera)}
              />

              {/* Fullscreen Stream Button */}
              <div className="absolute top-2 right-8">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onCameraClick?.(camera);
                  }}
                  className="rounded-lg transition-colors bg-black bg-opacity-50 text-white hover:bg-opacity-70 p-2"
                  title="Open Fullscreen Stream"
                >
                  <Eye className="h-4 w-4" />
                </button>
              </div>

              {/* Three Dots Menu */}
              {(onEditCamera || onDeleteCamera || onAssignCamera) && (
                <div className="absolute bottom-2 right-2">
                  <div className="relative dropdown-container">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDropdown(camera._id);
                      }}
                      className="bg-black bg-opacity-50 text-white hover:bg-opacity-70 rounded-lg transition-colors p-2"
                      title="More options"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>
                    
                    {/* Dropdown Menu */}
                    {openDropdown === camera._id && (
                      <div className="absolute right-0 bottom-full mb-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 min-w-[160px] w-48">
                        <div className="py-1">
                          {/* Playback */}
                          {hasPlaybackPermission && (
                            <Link
                              to={`/camera-playback?cameraId=${camera._id}`}
                              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                              onClick={closeDropdown}
                            >
                              <Clock className="h-4 w-4 mr-3 text-green-600 flex-shrink-0" />
                              <span className="truncate">Open Playback</span>
                            </Link>
                          )}
                          

                          {/* Edit Camera */}
                          {onEditCamera && (
                            <button
                              onClick={() => {
                                onEditCamera(camera);
                                closeDropdown();
                              }}
                              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                              <Edit className="h-4 w-4 mr-3 text-cyan-600 flex-shrink-0" />
                              <span className="truncate">Edit Camera</span>
                            </button>
                          )}

                          {/* Assign Camera */}
                          {onAssignCamera && (
                            <button
                              onClick={() => {
                                onAssignCamera(camera);
                                closeDropdown();
                              }}
                              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                              <Users className="h-4 w-4 mr-3 text-purple-600 flex-shrink-0" />
                              <span className="truncate">Assign Camera</span>
                            </button>
                          )}
                          
                          {/* Delete Camera */}
                          {onDeleteCamera && (
                            <button
                              onClick={() => {
                                onDeleteCamera(camera._id);
                                closeDropdown();
                              }}
                              className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <Trash2 className="h-4 w-4 mr-3 flex-shrink-0" />
                              <span className="truncate">Delete Camera</span>
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Camera Info */}
            <div className="p-3">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-medium text-gray-900 truncate flex-1 mr-2 text-sm" title={camera.name}>
                  {camera.name}
                </h3>
              </div>
              
              {camera.location?.exactLocation && (
                <p className="text-gray-600 mb-1 truncate text-xs" title={camera.location.exactLocation}>
                  📍 {camera.location.exactLocation}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Load More Button */}
      {visibleRange.end < filteredCameras.length && (
        <div className="text-center py-6">
          <button
            onClick={handleLoadMore}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
          >
            Load More Cameras ({filteredCameras.length - visibleRange.end} remaining)
          </button>
        </div>
      )}

      {/* Performance Info (Development Only) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="text-xs text-gray-500 bg-gray-100 p-2 rounded">
          <strong>Simplified Grid Info:</strong> 
          {filteredCameras.length} total cameras, showing {visibleRange.start + 1}-{visibleRange.end}
          <br />
          Grid layout: {gridLayout} | No react-window dependency
        </div>
      )}
    </div>
  );
};

export default SimplifiedCameraGrid;